import { useCallback, useMemo, useState } from 'react';
import { LayoutChangeEvent } from 'react-native';
import { scale } from '@components/frame';
import { TRACEABILITY_GRAPH_GEOMETRY } from '@v2/pages/common/components/TraceabilityGraph/constants';
import { TraceabilityCardCenters, TraceabilityColumn, TraceabilityConnector } from '@v2/pages/common/components/TraceabilityGraph/types';

// Owns the measure-then-draw cycle for the connector overlay.
// Column X positions are deterministic (derived from geometry); only each card's
// vertical center is measured, so the bezier curves attach exactly to the card ports.
export const useTraceabilityGraphLayout = (columns: TraceabilityColumn[]) => {
  const [cardCenters, setCardCenters] = useState<TraceabilityCardCenters>({});
  const [rowHeight, setRowHeight] = useState(0);

  const columnWidth = scale(TRACEABILITY_GRAPH_GEOMETRY.columnWidth);
  const columnGap = scale(TRACEABILITY_GRAPH_GEOMETRY.columnGap);
  const contentWidth = columns.length * columnWidth + Math.max(0, columns.length - 1) * columnGap;

  const onCardLayout = useCallback(
    (nodeId: string) => (event: LayoutChangeEvent) => {
      const { y, height } = event.nativeEvent.layout;
      const center = Math.round(y + height / 2);

      setCardCenters((prev) => (prev[nodeId] === center ? prev : { ...prev, [nodeId]: center }));
    },
    []
  );

  const onRowLayout = useCallback((event: LayoutChangeEvent) => {
    const height = Math.round(event.nativeEvent.layout.height);

    setRowHeight((prev) => (prev === height ? prev : height));
  }, []);

  const connectors = useMemo<TraceabilityConnector[]>(() => {
    const result: TraceabilityConnector[] = [];
    const seen = new Set<string>();

    for (let index = 0; index < columns.length - 1; index += 1) {
      const sources = columns[index].nodes;
      const targets = columns[index + 1].nodes;

      // Adjacent-only: an empty column leaves a real gap in the flow (deterministic layout).
      if (!sources.length || !targets.length) continue;

      const startX = index * (columnWidth + columnGap) + columnWidth;
      const endX = (index + 1) * (columnWidth + columnGap);
      const controlOffset = (endX - startX) * TRACEABILITY_GRAPH_GEOMETRY.curvature;

      // Pair cards by index; the shorter side fans out / merges into the longer one.
      const pairCount = Math.max(sources.length, targets.length);

      for (let pair = 0; pair < pairCount; pair += 1) {
        const source = sources[Math.min(pair, sources.length - 1)];
        const target = targets[Math.min(pair, targets.length - 1)];
        const id = `${source.nodeId}__${target.nodeId}`;

        if (seen.has(id)) continue;

        const startY = cardCenters[source.nodeId];
        const endY = cardCenters[target.nodeId];

        // Skip until both ends have been measured; the layout effect re-runs once they are.
        if (startY == null || endY == null) continue;

        seen.add(id);
        result.push({
          id,
          path: `M${startX},${startY} C${startX + controlOffset},${startY} ${endX - controlOffset},${endY} ${endX},${endY}`,
        });
      }
    }

    return result;
  }, [columns, cardCenters, columnWidth, columnGap]);

  return {
    connectors,
    onCardLayout,
    onRowLayout,
    contentWidth,
    rowHeight,
    columnWidth,
    columnGap,
  };
};
